const env = import.meta.env ?? {};
const FUNCTIONS_URL = normalizeBaseUrl(env.VITE_SUPABASE_FUNCTIONS_URL);
const SUPABASE_URL = normalizeBaseUrl(
  env.VITE_SUPABASE_URL ?? inferSupabaseUrl(FUNCTIONS_URL),
);
const SUPABASE_ANON_KEY = env.VITE_SUPABASE_ANON_KEY ?? "";

export const remoteLeaderboardEnabled = Boolean(FUNCTIONS_URL && SUPABASE_ANON_KEY);
export const supabaseAnonKey = SUPABASE_ANON_KEY;
export const supabaseAuthEnabled = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
export const supabaseUrl = SUPABASE_URL;

export async function startRemoteRun(payload) {
  if (!remoteLeaderboardEnabled) {
    return { remote: false };
  }

  return requestJson(`${FUNCTIONS_URL}/runs/start`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function completeRemoteRun(payload) {
  if (!remoteLeaderboardEnabled) {
    return { remote: false };
  }

  return requestJson(`${FUNCTIONS_URL}/runs/complete`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function finishRemoteRun(payload, accessToken) {
  if (!remoteLeaderboardEnabled) {
    return { remote: false };
  }

  if (!accessToken) {
    throw new Error("Log in to save your score.");
  }

  return requestJson(`${FUNCTIONS_URL}/runs/finish`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export async function fetchRemoteLeaderboard() {
  if (!remoteLeaderboardEnabled) {
    return null;
  }

  const response = await requestJson(`${FUNCTIONS_URL}/leaderboard`, {
    method: "GET",
  });

  return {
    ...response,
    leaders: normalizeLeaderboardEntries(response.leaders),
  };
}

export async function fetchRemoteProfile(accessToken) {
  if (!remoteLeaderboardEnabled || !accessToken) {
    return null;
  }

  return requestJson(`${FUNCTIONS_URL}/profile/me`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export function normalizeLeaderboardEntries(entries) {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries.map((entry, index) => normalizeLeaderboardEntry(entry, index));
}

function normalizeLeaderboardEntry(entry, index) {
  const rank = Number(entry?.rank);
  const score = Number(entry?.score);
  const name = typeof entry?.name === "string" && entry.name.trim()
    ? entry.name.trim()
    : "Player";
  const createdAt = typeof entry?.createdAt === "string"
    ? entry.createdAt
    : typeof entry?.created_at === "string"
      ? entry.created_at
      : null;

  return {
    createdAt,
    name,
    rank: Number.isInteger(rank) && rank > 0 ? rank : index + 1,
    score: Number.isFinite(score) ? score : 0,
  };
}

async function requestJson(url, options) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      ...(options.headers ?? {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const details = Array.isArray(data.reasons) && data.reasons.length
      ? ` (${data.reasons.join(", ")})`
      : "";
    throw new Error(`${data.error ?? `Request failed with ${response.status}`}${details}`);
  }

  return data;
}

function normalizeBaseUrl(value) {
  return value ? value.replace(/\/+$/, "") : "";
}

function inferSupabaseUrl(functionsUrl) {
  const match = functionsUrl.match(/^(https:\/\/[^/]+)\/functions\/v1$/);
  return match?.[1] ?? "";
}
